// UI pieces that only appear in the hosted (cloud) app: team, server-side AI key,
// couple links, file attachments, and portal-response notes. In local mode they render nothing.
import { useEffect, useState } from "react";
import { useStore, usePortalLinks } from "./lib/store.js";
import { callFunction } from "./lib/supabase.js";
import { callClaude, CLAUDE_MODELS } from "./lib/ai.js";
import { Dialog, Note } from "./ui.jsx";

const MAX_FILE_BYTES = 25 * 1024 * 1024;

function useCloud() {
  const store = useStore();
  return store.mode === "cloud" ? store : null;
}

async function copy(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) { return false; }
}

function LinkRow({ url }) {
  const [msg, setMsg] = useState("");
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <input className="input" readOnly aria-label="Link" value={url} onFocus={e => e.target.select()} style={{ flex: "1 1 260px", minWidth: 0, fontSize: 15 }} />
      <button className="btn btn-secondary" onClick={async () => setMsg(await copy(url) ? "Copied" : "Select and copy")}>Copy link</button>
      {msg && <span className="label" role="status">{msg}</span>}
    </div>
  );
}

/* ───────────── Settings: Email setup (gap #6, owner only) ───────────── */
export function EmailSetupSection() {
  const cloud = useCloud();
  const [st, setSt] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  useEffect(() => {
    if (cloud && cloud.isOwner) callFunction("team", { action: "email-status", studioId: cloud.studioId }).then(setSt).catch(e => setErr(e.message));
  }, []);
  if (!cloud || !cloud.isOwner) return null;

  const test = async () => {
    setBusy(true); setMsg(null);
    try { const r = await callFunction("team", { action: "test-email", studioId: cloud.studioId }); setMsg({ ok: true, text: "Sent to " + r.to + ". Check your inbox (and spam) in a minute." }); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };
  const Item = ({ label, on, value, missing }) => (
    <>
      <div>{label}</div>
      <div className="row" style={{ "--gap": "6px 10px" }}>
        <span className={"tag " + (on ? "tag-done" : "tag-neutral")}>{on ? "Set" : "Not set"}</span>
        <span style={{ overflowWrap: "anywhere" }}>{on ? value : missing}</span>
      </div>
    </>
  );

  return (
    <section className="stack" style={{ "--gap": "12px" }} aria-labelledby="email-setup-h">
      <div className="label" id="email-setup-h">Email setup</div>
      {err && <Note tone="warn">{err}</Note>}
      {!st && !err && <div className="field-hint">Checking…</div>}
      {st && (
        <>
          <div className="kv">
            <Item label="App address" on={!!st.appUrl} value={st.appUrl} missing={st.appUrlProblem} />
            <Item label="Email service" on={st.resend} value="Resend connected" missing="RESEND_API_KEY isn't set." />
            <Item label="Sends from" on={!!st.from} value={st.from} missing="EMAIL_FROM isn't set." />
          </div>
          {st.ready
            ? <div className="field-hint">Couples get their questionnaire and portal links by email, and your team gets invites.</div>
            : <Note tone="warn">
                Email isn&rsquo;t fully set up, so nothing is emailed yet{st.appUrl ? "; you’ll copy links and send them yourself" : ", and couple and team links can’t be made"}.
                Add the missing values in Supabase under <strong>Edge Functions → Secrets</strong> (SETUP.md §2).
              </Note>}
          <div className="field-hint">Password-reset and sign-in emails are sent by Supabase itself. They arrive only once the same Resend account is added under <strong>Authentication → Emails → SMTP</strong> (SETUP.md §1.6). Before launch, send a real password reset to each planner.</div>
          <div className="row" style={{ "--gap": "10px 16px" }}>
            <button className="btn btn-secondary" onClick={test} disabled={busy || !st.ready}>{busy ? "Sending…" : "Send me a test email"}</button>
            {msg && <span role="status" className={msg.ok ? "" : "error-text"} style={{ fontSize: 15 }}>{msg.text}</span>}
          </div>
        </>
      )}
    </section>
  );
}

/* ───────────── Settings: AI on the server ───────────── */
export function CloudSettingsSection({ model, onModel, readOnly }) {
  const cloud = useCloud();
  const [info, setInfo] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [testMsg, setTestMsg] = useState("");
  const [showHelp, setShowHelp] = useState(false);

  const load = () => callFunction("team", { action: "ai-status", studioId: cloud.studioId }).then(setInfo).catch(e => setMsg(e.message));
  useEffect(() => { if (cloud) load(); }, []);
  if (!cloud) return null;

  const saveKey = async remove => {
    setBusy(true); setMsg("");
    try {
      await callFunction("team", { action: "set-ai-key", studioId: cloud.studioId, key: remove ? "" : keyInput.trim() });
      setKeyInput("");
      setMsg(remove ? "Key removed." : "Key saved on the server.");
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const test = async () => {
    setTestMsg("Testing…");
    try { const { text } = await callClaude(null, { prompt: "Reply with the single word: OK", maxTokens: 8 }); setTestMsg("Connected" + (text ? " — model replied: " + text.trim().slice(0, 20) : "") + "."); }
    catch (e) { setTestMsg(e.message || "Connection failed."); }
  };
  const u = info && info.usage;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="label">AI (runs on the server)</div>
      <Note>
        Quotes and contracts are drafted on the server — the Anthropic key is never sent to anyone&rsquo;s browser.
        {info && (info.hasStudioKey
          ? <> Using the studio&rsquo;s own key (ending in <strong>{info.last4}</strong>).</>
          : info.hasFallback ? <> Using the setup key from Hii.Frequency Media until the studio adds its own.</> : <> <strong>No key yet</strong> — add one below.</>)}
      </Note>
      {u && (
        <div className="text-muted" style={{ fontSize: 15 }}>
          This month: {u.calls} AI request{u.calls === 1 ? "" : "s"} · {(u.inputTokens + u.outputTokens).toLocaleString()} tokens
          {info.cap ? <> of the {info.cap.toLocaleString()}-token monthly cap</> : null}
        </div>
      )}
      {!readOnly && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input className="input" type="password" autoComplete="off" aria-label="Anthropic API key" placeholder={info && info.hasStudioKey ? "Paste a new key to replace it" : "sk-ant-…"} value={keyInput} onChange={e => setKeyInput(e.target.value)} style={{ flex: "1 1 240px" }} />
            <button className="btn btn-secondary" disabled={busy || keyInput.trim().length < 20} onClick={() => saveKey(false)}>{busy ? "Saving…" : "Save key"}</button>
            {info && info.hasStudioKey && <button className="btn btn-secondary" disabled={busy} onClick={() => { if (window.confirm("Remove the studio's API key?")) saveKey(true); }}>Remove</button>}
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button className="btn btn-secondary" onClick={test}>Test connection</button>
            <button className="btn-link" onClick={() => setShowHelp(s => !s)}>{showHelp ? "Hide steps" : "How do I get a key?"}</button>
            {(msg || testMsg) && <span role="status" style={{ fontSize: 15 }}>{msg || testMsg}</span>}
          </div>
          {showHelp && (
            <Note>
              <ol style={{ margin: "0 0 0 18px", padding: 0 }}>
                <li>Go to <strong>console.anthropic.com</strong>, sign up, and add a card under <strong>Billing</strong> (pay-as-you-go; each quote or contract costs a few cents).</li>
                <li>Set a monthly <strong>spend limit</strong> in Billing.</li>
                <li>Open <strong>API keys</strong> → <strong>Create key</strong>, name it &ldquo;GhostWriter Events app&rdquo;, copy it, and paste it above.</li>
              </ol>
            </Note>
          )}
        </>
      )}
      <label style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="field-label">Model</span>
        <select className="input" value={model} disabled={readOnly} onChange={e => onModel(e.target.value)}>
          {CLAUDE_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </label>
    </div>
  );
}

/* ───────────── Settings: team ───────────── */
export function TeamSection() {
  const cloud = useCloud();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("planner");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [lastLink, setLastLink] = useState("");

  const load = () => callFunction("team", { action: "list", studioId: cloud.studioId }).then(setData).catch(e => setMsg(e.message));
  useEffect(() => { if (cloud) load(); }, []);
  if (!cloud) return null;

  const invite = async () => {
    setBusy(true); setMsg(""); setLastLink("");
    try {
      const r = await callFunction("team", { action: "invite", studioId: cloud.studioId, email: email.trim(), role });
      setEmail("");
      setLastLink(r.url);
      setMsg(r.emailed ? "Invite emailed to " + r.email + "." : "Invite created — email isn't set up yet, so send them this link:");
      await load();
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const act = async (action, extra, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setMsg("");
    try { await callFunction("team", { action, studioId: cloud.studioId, ...extra }); await load(); } catch (e) { setMsg(e.message); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="label">Team</div>
      {!data && !msg && <div className="text-muted" style={{ fontSize: 15 }}>Loading…</div>}
      {data && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {data.members.map(m => (
            <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15.5, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 500 }}>{m.name || m.email}</span>
              {m.name && <span className="text-muted">{m.email}</span>}
              <span className={"tag " + (m.role === "owner" ? "tag-done" : "tag-neutral")}>{m.role === "owner" ? "Owner" : "Planner"}</span>
              {m.userId === cloud.userId && <span className="text-muted" style={{ fontSize: 14 }}>(you)</span>}
              {cloud.isOwner && m.userId !== cloud.userId && (
                <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                  <button className="btn btn-secondary" onClick={() => act("set-role", { userId: m.userId, role: m.role === "owner" ? "planner" : "owner" })}>{m.role === "owner" ? "Make planner" : "Make owner"}</button>
                  <button className="btn btn-secondary" onClick={() => act("remove", { userId: m.userId }, "Remove " + (m.name || m.email) + " from the studio? They lose access immediately.")}>Remove</button>
                </span>
              )}
            </div>
          ))}
          {data.invites.map(iv => (
            <div key={iv.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 15.5, flexWrap: "wrap" }}>
              <span>{iv.email}</span>
              <span className="tag tag-outline">Invited · {iv.role === "owner" ? "Owner" : "Planner"}</span>
              {cloud.isOwner && <button className="btn-link" style={{ marginLeft: "auto" }} onClick={() => act("revoke-invite", { inviteId: iv.id })}>Cancel invite</button>}
            </div>
          ))}
        </div>
      )}
      {cloud.isOwner && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input className="input" type="email" aria-label="Teammate email" placeholder="teammate@email.com" value={email} onChange={e => setEmail(e.target.value)} style={{ flex: "1 1 220px" }} />
          <select className="input" aria-label="Role" value={role} onChange={e => setRole(e.target.value)} style={{ width: 150 }}>
            <option value="planner">Planner</option>
            <option value="owner">Owner</option>
          </select>
          <button className="btn btn-secondary" disabled={busy || !email.includes("@")} onClick={invite}>{busy ? "Inviting…" : "Invite"}</button>
        </div>
      )}
      {cloud.isOwner && <div className="text-muted" style={{ fontSize: 14 }}>Planners can work on every client, quote and contract. Only owners can change settings, the AI key, and the team.</div>}
      {msg && <div role="status" style={{ fontSize: 15 }}>{msg}</div>}
      {lastLink && <LinkRow url={lastLink} />}
      <button className="btn-link" style={{ alignSelf: "flex-start" }} onClick={() => { if (window.confirm("Sign out of every device and browser?")) cloud.signOut("global"); }}>Sign out everywhere</button>
    </div>
  );
}

/* ───────────── Share intake form ───────────── */
export function ShareIntakeDialog({ onClose, onFillHere }) {
  const cloud = useCloud();
  const [names, setNames] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  if (!cloud) return null;
  const publicUrl = location.origin + "/intake";

  const send = async () => {
    setBusy(true); setErr(""); setResult(null);
    try { setResult(await callFunction("intake", { action: "create-invite", studioId: cloud.studioId, email: email.trim(), names: names.trim() })); setEmail(""); setNames(""); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog title="Share the intake form" onClose={onClose}>
      <div className="label">Public &ldquo;Get started&rdquo; link</div>
      <div style={{ fontSize: 15.5, lineHeight: 1.5 }}>For the website button or social bio. Anyone can start it; couples get an email with a link to resume where they left off.</div>
      <LinkRow url={publicUrl} />
      <hr className="rule" />
      <div className="label">Send a personal invite</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input className="input" aria-label="Couple's names (optional)" placeholder="Couple's names (optional)" value={names} onChange={e => setNames(e.target.value)} style={{ flex: "1 1 180px" }} />
        <input className="input" type="email" aria-label="Their email" placeholder="Their email" value={email} onChange={e => setEmail(e.target.value)} style={{ flex: "1 1 200px" }} />
        <button className="btn btn-primary" disabled={busy || !email.includes("@")} onClick={send}>{busy ? "Sending…" : "Send invite"}</button>
      </div>
      {err && <Note tone="warn">{err}</Note>}
      {result && (
        <>
          <Note tone="ok">{result.emailed ? "Invite emailed to " + result.email + ". You can also share this link:" : "Link created — email isn't set up yet, so send them this link yourself:"}</Note>
          <LinkRow url={result.url} />
        </>
      )}
      <hr className="rule" />
      <button className="btn btn-secondary" style={{ alignSelf: "flex-start" }} onClick={onFillHere}>Fill it in here instead (e.g. on a call)</button>
    </Dialog>
  );
}

/* ───────────── Couple portal link ───────────── */
export function CoupleLinksDialog({ client, onClose, onPreview }) {
  const cloud = useCloud();
  const links = usePortalLinks() || {};
  const link = links[client.id];
  const emails = (client.partners || []).map(p => p.email).filter(Boolean);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  if (!cloud) return null;

  const create = async sendEmail => {
    setBusy(true); setMsg("");
    try {
      const r = await callFunction("portal", { action: "create-link", studioId: cloud.studioId, clientId: client.id, email: sendEmail });
      await cloud.loadPortalLinks();
      setMsg(sendEmail ? (r.emailed ? "Portal link emailed to " + r.emailedTo.join(" and ") + "." : "Email isn't set up yet — copy the link below and send it yourself.") : "Portal link ready.");
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Turn off this link? The couple won't be able to open their portal until you create a new one.")) return;
    setBusy(true); setMsg("");
    let err = null;
    try { await cloud.revokePortalLink(client.id); } catch (e) { err = e; }
    setBusy(false);
    setMsg(err ? err.message : "Link turned off.");
  };

  return (
    <Dialog title={client.names + " · couple portal"} onClose={onClose}>
      <div style={{ fontSize: 15.5, lineHeight: 1.55 }}>
        The portal shows the couple their wedding details, timeline, budget and vendors — and lets them approve their proposal and sign their agreement once you&rsquo;ve marked them as sent. No password needed; the link is private to them.
      </div>
      {link ? (
        <>
          <LinkRow url={link.url} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {emails.length > 0 && <button className="btn btn-primary" disabled={busy} onClick={() => create(true)}>Email link to {emails.length > 1 ? "both" : emails[0]}</button>}
            <button className="btn btn-secondary" disabled={busy} onClick={revoke}>Turn off link</button>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn btn-primary" disabled={busy} onClick={() => create(false)}>{busy ? "Creating…" : "Create portal link"}</button>
          {emails.length > 0 && <button className="btn btn-secondary" disabled={busy} onClick={() => create(true)}>Create &amp; email it</button>}
        </div>
      )}
      {msg && <Note>{msg}</Note>}
      <hr className="rule" />
      <button className="btn-link" style={{ alignSelf: "flex-start" }} onClick={onPreview}>Preview what the couple sees</button>
    </Dialog>
  );
}

/* ───────────── Documents: file attachments ───────────── */
export function DocFileField({ clientId, value, onChange }) {
  const cloud = useCloud();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!cloud) return null;

  const pick = async e => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_FILE_BYTES) { setErr("That file is over 25 MB."); return; }
    setBusy(true); setErr("");
    try { onChange(await cloud.uploadDocFile(clientId, f)); } catch (x) { setErr(x.message || "Upload failed."); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 15.5 }}>
      <label className="btn btn-secondary" style={{ cursor: busy ? "default" : "pointer" }}>
        {busy ? "Uploading…" : value ? "Replace file" : "Attach file (PDF, image, doc)"}
        <input type="file" onChange={pick} disabled={busy} style={{ display: "none" }} />
      </label>
      {value && <span className="text-muted">{value.name} · {Math.max(1, Math.round(value.size / 1024))} KB</span>}
      {value && <button className="btn-link" onClick={() => onChange(null)}>Detach</button>}
      {err && <span role="alert" className="error-text">{err}</span>}
    </div>
  );
}

export function DocFileLink({ file }) {
  const cloud = useCloud();
  if (!cloud || !file || !file.path) return null;
  const open = async () => {
    const w = window.open("", "_blank");
    try { const url = await cloud.docFileUrl(file.path); if (w) w.location = url; else location.href = url; }
    catch (e) { if (w) w.close(); alert(e.message || "Couldn't open the file."); }
  };
  return <button className="tag tag-outline" style={{ cursor: "pointer", background: "transparent" }} onClick={open} title={file.name} aria-label={"Open file " + file.name}>Open file</button>;
}

/* ───────────── Quote & contract notes from the portal ───────────── */
export function ChangeRequestNote({ item }) {
  if (!item || !item.changeRequest || item.status !== "Draft") return null;
  const at = new Date(item.changeRequest.at).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return <Note tone="warn"><strong>The couple asked for changes ({at}):</strong> &ldquo;{item.changeRequest.comment}&rdquo; — edit, approve, and send again when ready.</Note>;
}

// Signed online before Phase 11 · Step 4 (one signer). Newer agreements show SigningStatus instead.
export function SignatureNote({ contract }) {
  if (!contract || !contract.signature || (contract.signatures && contract.signatures.length)) return null;
  const at = new Date(contract.signature.at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  return <Note tone="ok">✓ Signed online by <strong>{contract.signature.name}</strong> on {at}. The signed copy is filed in Documents.</Note>;
}
